if (!customElements.get('product-card-quick-bar')) {
  class ProductCardQuickBar extends HTMLElement {
    #abortController = null;

    connectedCallback() {
      this.productCard = this.closest('product-card');
      this.config = JSON.parse(this.querySelector('[data-config]').textContent.trim());
      this.productForm = this.querySelector('product-form');
      this.form = this.productForm?.querySelector('form[action*="/cart/add"]');
      this.idInput = this.form?.querySelector('[name="id"]');
      this.sizeButtons = [...this.querySelectorAll('[data-size]')];
      this.submitButton = this.querySelector('.product-card__quick-bar-submit');
      this.#abortController = new AbortController();
      const { signal } = this.#abortController;

      this.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest('[data-size]');

          if (!button || !this.contains(button)) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();
          this.selectSize(button);
        },
        { signal }
      );

      if (this.productCard) {
        this.productCard.addEventListener('mouseenter', () => this.showQuickBar(), { signal });
        this.productCard.addEventListener('mouseleave', () => this.hideQuickBar(), { signal });

        this.productCard.addEventListener(
          'change',
          (event) => {
            if (event.target.matches('.product-card__info [type="radio"]')) {
              this.syncSizes();
            }
          },
          { signal }
        );
      }

      if (this.form) {
        this.form.addEventListener(
          'submit',
          () => {
            this.updateVariantIdFromSelection();
          },
          { capture: true, signal }
        );

        this.form.addEventListener('variant:add', () => this.hideQuickBar(), { signal });
      }

      if (this.sizeButtons.length > 0) {
        this.syncSizes();
      }

      this.addEventListener(
        'click',
        (event) => {
          event.stopPropagation();
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.#abortController?.abort();
      this.hideQuickBar();
    }

    showQuickBar() {
      this.productCard?.classList.add('is-quick-bar-visible');
    }

    hideQuickBar() {
      this.productCard?.classList.remove('is-quick-bar-visible');

      if (this.contains(document.activeElement)) {
        document.activeElement.blur();
      }
    }

    switchProduct(quickBuyConfig) {
      this.config = quickBuyConfig;
      this.rebuildSizeButtons();
      this.syncSizes();
    }

    rebuildSizeButtons() {
      const container = this.querySelector('.product-card__quick-bar-sizes');
      const sizes = this.config.sizes || [];

      if (!container) {
        this.sizeButtons = [];
        this.updateVariantIdFromSelection();
        return;
      }

      if (sizes.length === 0) {
        container.innerHTML = '';
        container.hidden = true;
        this.sizeButtons = [];
        this.updateVariantIdFromSelection();
        return;
      }

      container.hidden = false;
      container.setAttribute('aria-label', this.config.sizeOptionName || 'Size');

      container.innerHTML = sizes
        .map(
          (sizeName) =>
            `<button type="button" class="product-card__quick-bar-size block-underline text-sm" data-size="${this.escapeAttribute(
              sizeName
            )}">${this.escapeHtml(sizeName)}</button>`
        )
        .join('');

      this.sizeButtons = [...container.querySelectorAll('[data-size]')];
    }

    escapeAttribute(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }

    getSelectedColorName() {
      if (this.config.colorIndex == null) {
        return null;
      }

      const connectedColors = this.productCard?.querySelector('product-card-connected-colors');

      if (connectedColors) {
        return this.config.initialColor;
      }

      const checked = this.productCard?.querySelector('.product-card__info [type="radio"]:checked');

      if (checked && this.config.optionValueIdToName[checked.value]) {
        return this.config.optionValueIdToName[checked.value];
      }

      return this.config.initialColor;
    }

    findVariant(sizeName, colorName) {
      return this.config.variants.find((variant) => {
        if (sizeName != null && this.config.sizeIndex != null && variant.options[this.config.sizeIndex] !== sizeName) {
          return false;
        }

        if (colorName != null && this.config.colorIndex != null && variant.options[this.config.colorIndex] !== colorName) {
          return false;
        }

        return true;
      });
    }

    isVariantAvailable(sizeName, colorName) {
      const variant = this.findVariant(sizeName, colorName);
      return Boolean(variant?.available);
    }

    syncSizes() {
      const colorName = this.getSelectedColorName();
      let firstAvailableButton = null;

      this.sizeButtons.forEach((button) => {
        const available = this.isVariantAvailable(button.dataset.size, colorName);

        button.classList.toggle('is-disabled', !available);
        button.toggleAttribute('disabled', !available);
        button.setAttribute('aria-disabled', available ? 'false' : 'true');

        if (available && !firstAvailableButton) {
          firstAvailableButton = button;
        }
      });

      const selectedAvailable = this.sizeButtons.find(
        (button) => button.classList.contains('is-selected') && !button.classList.contains('is-disabled')
      );

      if (selectedAvailable) {
        this.selectSize(selectedAvailable, false);
      } else if (firstAvailableButton) {
        this.selectSize(firstAvailableButton, false);
      } else if (this.idInput) {
        const fallback = this.config.variants.find((variant) => {
          if (colorName != null && this.config.colorIndex != null && variant.options[this.config.colorIndex] !== colorName) {
            return false;
          }

          return variant.available;
        });

        if (fallback) {
          this.idInput.value = fallback.id;
        }
      }
    }

    updateVariantIdFromSelection() {
      const selectedButton = this.sizeButtons.find((button) => button.classList.contains('is-selected'));

      if (selectedButton) {
        const variant = this.findVariant(selectedButton.dataset.size, this.getSelectedColorName());

        if (variant && this.idInput) {
          this.idInput.value = variant.id;
        }

        return;
      }

      if (this.sizeButtons.length === 0 && this.idInput) {
        const colorName = this.getSelectedColorName();
        const variant = this.config.variants.find((entry) => {
          if (colorName != null && this.config.colorIndex != null && entry.options[this.config.colorIndex] !== colorName) {
            return false;
          }

          return entry.available;
        });

        if (variant) {
          this.idInput.value = variant.id;
        }
      }
    }

    selectSize(button, scrollIntoView = false) {
      if (button.classList.contains('is-disabled')) {
        return;
      }

      this.sizeButtons.forEach((sizeButton) => {
        sizeButton.classList.remove('is-selected');
      });

      button.classList.add('is-selected');
      this.updateVariantIdFromSelection();

      if (scrollIntoView) {
        button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }
  }

  customElements.define('product-card-quick-bar', ProductCardQuickBar);
}
